import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';

import clsx from 'clsx';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import WithListObservers from '../../../hoc/with-list-observers';
import type { ICommunitiesViewCommunity } from '../../../redux/api/communities/communities-api.types';
import { Paths } from '../../../shared/paths';
import AppAddPillButton from '../../designe/buttons/app-add-pill-button/AppAddPillButton';
import NoResults from '../no-results';
import Search from '../search/Search';
import CommunityListItemSkeleton from '../skeletons/community-list-item-skeleton/CommunityListItemSkeleton';
import { useStyles } from './CommunitiesList.styles';
import CommunityBigItem from './community-big-item/CommunityBigItem';

const DEFAULT_COMMUNITY_LIST_SKELETON_COUNT = 4;

export interface ICommunitiesListBigProps {
  communities: ICommunitiesViewCommunity[] | null | undefined;
  showSearch?: boolean;
  showAddButton?: boolean;
  listFooter?: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  skeletonCount?: number;
  withoutTabs?: boolean;
}

export default function CommunitiesListBig(props: ICommunitiesListBigProps) {
  const {
    communities,
    showSearch = false,
    showAddButton = false,
    listFooter,
    isLoading = false,
    isError = false,
    skeletonCount = DEFAULT_COMMUNITY_LIST_SKELETON_COUNT,
    withoutTabs = false,
  } = props;
  const { classes } = useStyles();
  const [filterValue, setFilterValue] = useState<string>('');
  const [filteredCommunities, setFilteredCommunities] = useState<
    ICommunitiesViewCommunity[] | null
  >(communities);
  const { navigate } = useNavigationStack();

  useEffect(() => {
    if (!communities) {
      setFilteredCommunities(null);
      return;
    }
    if (!filterValue) {
      setFilteredCommunities(communities);
    } else {
      const valueLowerCase = filterValue.toLowerCase();
      setFilteredCommunities(
        communities.filter(
          (c) => c.title?.toLowerCase().indexOf(valueLowerCase) !== -1,
        ),
      );
    }
  }, [filterValue, communities]);

  function addNewExerciseClicked() {
    navigate(Paths.communities.toNew);
  }

  const awaitingListData = communities == null && !isError;
  const showSkeleton = Boolean(isLoading) || awaitingListData;
  const showNoResult =
    !showSkeleton && !!filteredCommunities && !filteredCommunities?.length;
  const listClassName = clsx(
    withoutTabs
      ? classes.communityBigItemWrapperWithoutTabs
      : classes.communityBigItemWrapper,
    showAddButton &&
      (withoutTabs
        ? classes.communityBigItemWrapperWithAddButtonWithoutTabs
        : classes.communityBigItemWrapperWithAddButton),
  );

  return (
    <div className={classes.communitiesListWrapper}>
      {showSearch && (
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="flex-end"
          gap={1}>
          <Search
            fullWidth
            value={filterValue}
            onChange={setFilterValue}
            className={classes.communitiesListSearch}
          />
          {showAddButton && (
            <AppAddPillButton
              onClick={() => addNewExerciseClicked()}
              colorVariant="primary"
            />
          )}
        </Box>
      )}
      {showSkeleton && (
        <Box className={listClassName}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <CommunityListItemSkeleton
              key={`community-list-skeleton-${index}`}
            />
          ))}
          {listFooter}
        </Box>
      )}
      {!showSkeleton && showNoResult && <NoResults />}
      {!showSkeleton && !showNoResult && (
        <WithListObservers wrapperClassName={listClassName}>
          {filteredCommunities?.map((c) => (
            <React.Fragment key={c.id}>
              <CommunityBigItem community={c} />
            </React.Fragment>
          ))}
          {listFooter}
        </WithListObservers>
      )}
    </div>
  );
}
